import { useRef, useState } from 'react';
import { Send, Paperclip } from 'lucide-react';
import { Agent } from '../../../types/agent';

interface ProjectChatInputProps {
  selectedAgent: Agent | null;
  onOpenAgentSelector: () => void;
  onSubmit: (question: string) => void;
  onFileSelect?: (files: File[]) => void;
  isCreating?: boolean;
  disabled?: boolean;
}

/**
 * ProjectChatInput - Campo de input para criar nova conversa no projeto
 */
export const ProjectChatInput = ({
  selectedAgent,
  onOpenAgentSelector,
  onSubmit,
  onFileSelect,
  isCreating = false,
  disabled = false
}: ProjectChatInputProps) => {
  const [question, setQuestion] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!question.trim() || isCreating || !selectedAgent) return;
    onSubmit(question);
    setQuestion('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onFileSelect) {
      onFileSelect(Array.from(files));
    }
    e.target.value = '';
  };

  return (
    <div className="px-8 py-6">
      <form onSubmit={handleSubmit}>
        <div className="border-2 border-[#F4CC04] rounded-xl bg-[#1a1a1a] overflow-hidden">
          {/* Input area */}
          <div className="p-4">
            <textarea
              ref={textareaRef}
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                e.target.style.height = 'inherit';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
              }}
              onKeyDown={handleKeyDown}
              placeholder={`Inicie uma nova conversa neste projeto...`}
              disabled={isCreating || disabled}
              rows={1}
              className="
                w-full bg-transparent text-white text-sm
                placeholder:text-[#666666] resize-none
                focus:outline-none
              "
            />
          </div>

          {/* Bottom bar */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#333333]">
            {/* Left side: Agent selector + File attach */}
            <div className="flex items-center gap-3">
              {/* File attach button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isCreating || disabled}
                className="p-2 text-[#666666] hover:text-white transition-colors"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.txt,.csv,.json,.xml"
              />

              {/* Agent selector */}
              <button
                type="button"
                onClick={onOpenAgentSelector}
                disabled={isCreating || disabled}
                className="
                  flex items-center gap-2 px-3 py-1.5
                  bg-[#2a2a2a] rounded-lg text-sm text-white
                  hover:bg-[#333333] transition-colors
                "
              >
                <span>{selectedAgent?.name || 'Selecione um agente'}</span>
              </button>
            </div>

            {/* Right side: Send button */}
            <button
              type="submit"
              disabled={isCreating || disabled || !question.trim() || !selectedAgent}
              className="
                p-2 rounded-lg bg-[#F4CC04] text-[#1a1a1a]
                hover:bg-[#f5d633] transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              {isCreating ? (
                <div className="w-5 h-5 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
